// Full-page Doctor × Month time-off heatmap. Reached from the left sidebar
// ("Time Off"). It owns all view state, fetches its own doctor/tenant options
// (it lives outside the calendar shell), and reuses the day-view's data hook
// (`useTimeOffList`), form (`TimeOffFormSheet`), and import dialog
// (`ImportHolidaysDialog`).
//
// Patterned on `slots-heatmap/slots-heatmap-page.jsx` — same page header,
// toolbar layout, matrix + detail-rail two-pane body, and delete-confirm
// dialog. The differences (year nav instead of week nav, type/scope chips
// instead of tenant chips, no bulk-color) are deliberate adaptations to the
// time-off data shape (multi-day spans, multiple types, no per-entry color).

import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { AlertTriangle, CalendarPlus, Download, Globe2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useHasPermission } from "@/hooks/use-permissions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { fetchTenants } from "@/lib/api/tenants";
import { filterClinicsForSurface } from "@/lib/clinic-visibility";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import { fetchDoctors } from "@/lib/api/users";
import { deleteAdminTimeOff } from "@/lib/api/admin-time-off";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { useTimeOffList } from "../day-view/use-time-off-list";
import { TimeOffFormSheet } from "../day-view/time-off-form-sheet";
import { ImportHolidaysDialog } from "../day-view/import-holidays-dialog";
import { Toolbar } from "./toolbar";
import { TimeOffHeatmapMatrix } from "./time-off-heatmap-matrix";
import { DetailRail } from "./detail-rail";
import {
  entryIntersectsMonth,
  formatEntryRange,
  yearRange,
} from "./time-off-utils";

const GLOBAL_KEY = "__global__";

// Map a raw tenants API item to the option shape `useTimeOffList` /
// `TimeOffFormSheet` expect (mirrors slots-heatmap-page.jsx and calendar.jsx).
function toTenantOption(item) {
  const id = String(item?.id || item?._id || "").trim();
  if (!id) return null;
  const name = resolveTenantDisplayName(item, id);
  const country = String(item?.country || "").trim().toUpperCase();
  const timezone = String(item?.timezone || "").trim();
  return { id, name, country, timezone };
}

// Map a raw doctors API item to the option shape (mirrors slots-heatmap-page.jsx).
function toDoctorOption(item) {
  const id = String(item?.id || item?._id || "").trim();
  if (!id) return null;
  const firstName = String(item?.firstName || "").trim();
  const lastName = String(item?.lastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = String(item?.displayName || "").trim();
  return { id, name: fullName || displayName || id };
}

function TimeOffHeatmapPage() {
  // RBAC: scheduling:view keeps the year heatmap read-only; manage unlocks create/import/edit.
  const canManage = useHasPermission("scheduling", "manage");
  // Visible year. Default to the current calendar year — `useTimeOffList`
  // refetches whenever fromDate/toDate change, so cycling years triggers a
  // network request.
  const [year, setYear] = useState(() => DateTime.now().year);
  const { from: fromDate, to: toDate } = useMemo(() => yearRange(year), [year]);
  const isCurrentYear = year === DateTime.now().year;

  const [activeCell, setActiveCell] = useState(null); // { doctorId, month } | null
  const [typeFilter, setTypeFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [timeOffForm, setTimeOffForm] = useState({
    open: false,
    mode: "create",
    entry: null,
    selectedDate: null,
    defaultScope: "doctor",
  });
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [doctorOptions, setDoctorOptions] = useState([]);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);

  // Debounce the search box (200ms) before it reaches the data hook.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 200);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Standalone page → fetch its own doctor/tenant options.
  useEffect(() => {
    let isMounted = true;
    Promise.all([fetchTenants(), fetchDoctors()])
      .then(([tenantResponse, doctorResponse]) => {
        if (!isMounted) return;
        // Clinics hidden on the "calendar" surface (Settings → Clinic visibility) stay out.
        const tenants = filterClinicsForSurface(tenantResponse?.items, "calendar")
          .map(toTenantOption)
          .filter(Boolean);
        const doctors = (Array.isArray(doctorResponse?.items) ? doctorResponse.items : [])
          .map(toDoctorOption)
          .filter(Boolean);
        setTenantOptions(tenants);
        setDoctorOptions(doctors);
        setOptionsError(null);
      })
      .catch((error) => {
        if (!isMounted) return;
        setTenantOptions([]);
        setDoctorOptions([]);
        setOptionsError(error);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const { rows, groups, isLoading, error } = useTimeOffList({
    fromDate,
    toDate,
    search,
    scopeFilter,
    typeFilter,
    sourceFilter: "all",
    groupBy: "doctor",
    sortBy: "dateAsc",
    refreshKey,
    doctorOptions,
    tenantOptions,
  });

  // First tenant powers the import dialog's defaults (country / tenantId).
  // The list itself isn't scoped to a single tenant.
  const importDefaults = useMemo(() => tenantOptions[0] || null, [tenantOptions]);

  // Doctor backing the detail rail. For the Global row we synthesize a "doctor"
  // shape so the rail renders the same way ({ id: "__global__", name: "Global" }).
  const activeDoctor = useMemo(() => {
    if (!activeCell) return null;
    if (activeCell.doctorId === GLOBAL_KEY) {
      return { id: GLOBAL_KEY, name: "Global" };
    }
    const group = groups.find((entry) => entry.key === activeCell.doctorId);
    return group ? { id: group.key, name: group.label } : null;
  }, [groups, activeCell]);

  // Rows shown in the rail: the active doctor's (or global's) entries that
  // intersect the active month. We compute against `groups` (already
  // scope/type/search-filtered) so the rail respects the toolbar's filters.
  const activeRows = useMemo(() => {
    if (!activeCell || !activeDoctor) return [];
    const group = groups.find((entry) => entry.key === activeCell.doctorId);
    if (!group) return [];
    const monthStart = DateTime.fromObject({
      year,
      month: activeCell.month,
      day: 1,
    }).startOf("month");
    return group.rows.filter((row) => entryIntersectsMonth(row, monthStart));
  }, [activeCell, activeDoctor, groups, year]);

  const handleNew = (scope = "doctor") => {
    setTimeOffForm({
      open: true,
      mode: "create",
      entry: null,
      selectedDate: DateTime.now(),
      defaultScope: scope === "global" ? "global" : "doctor",
    });
  };

  // Add-from-cell: seed the form's scope from the active cell so clicking the
  // CTA in the Global row creates a global entry (and the Doctor row creates
  // a per-doctor one) — no extra clicks for the common case.
  const handleAddToActiveMonth = () => {
    if (!activeCell) return;
    const monthStart = DateTime.fromObject({
      year,
      month: activeCell.month,
      day: 1,
    }).startOf("month");
    setTimeOffForm({
      open: true,
      mode: "create",
      entry: null,
      selectedDate: monthStart,
      defaultScope: activeCell.doctorId === GLOBAL_KEY ? "global" : "doctor",
    });
  };

  const handleEdit = (row) => {
    setTimeOffForm({
      open: true,
      mode: "edit",
      entry: row,
      selectedDate: null,
      defaultScope: row?.scope === "global" ? "global" : "doctor",
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteAdminTimeOff({ id: deleteTarget.id });
      toast.success("Time off deleted.");
      setDeleteTarget(null);
      setRefreshKey((value) => value + 1);
      publishCalendarRefresh({ source: "time-off-delete" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete.");
    } finally {
      setIsDeleting(false);
    }
  };

  const errorIsAuth = useMemo(() => {
    if (!error) return false;
    const status = error?.status ?? null;
    if (status === 401 || status === 403) return true;
    const msg = String(error?.message || "").toLowerCase();
    return msg.includes("unauthorized") || msg.includes("forbidden");
  }, [error]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background text-foreground">
      {/* Page header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border bg-card/40 px-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Calendar</span>
          <span className="text-muted-foreground/60">/</span>
          <span className="font-semibold text-foreground">
            Time off · {year}
          </span>
          <span className="text-muted-foreground/60">
            · {rows.length} {rows.length === 1 ? "entry" : "entries"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="[&_svg]:size-3.5"
            onClick={() => setRefreshKey((value) => value + 1)}
          >
            <RefreshCw className={isLoading ? "animate-spin" : undefined} />
            Refresh
          </Button>
          {canManage ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="[&_svg]:size-3.5"
                onClick={() => setIsImportOpen(true)}
              >
                <Download />
                Import holidays
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="[&_svg]:size-3.5"
                onClick={() => handleNew("global")}
                title="Create a company-wide block that affects every doctor at a location"
              >
                <Globe2 />
                New global
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                className="[&_svg]:size-3.5"
                onClick={() => handleNew("doctor")}
              >
                <CalendarPlus />
                New time off
              </Button>
            </>
          ) : null}
        </div>
      </header>

      <Toolbar
        year={year}
        isCurrentYear={isCurrentYear}
        onPrevYear={() => setYear((y) => y - 1)}
        onNextYear={() => setYear((y) => y + 1)}
        onThisYear={() => setYear(DateTime.now().year)}
        typeFilter={typeFilter}
        onTypeFilter={setTypeFilter}
        scopeFilter={scopeFilter}
        onScopeFilter={setScopeFilter}
        search={searchInput}
        onSearchChange={setSearchInput}
      />

      {optionsError ? (
        <div className="border-b border-border bg-destructive/10 px-5 py-2 text-xs text-destructive">
          Couldn't load doctors and locations — names may be incomplete.
        </div>
      ) : null}

      {errorIsAuth ? (
        <div className="border-b border-amber-300 bg-amber-50 px-5 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200">
          You need admin access to view and edit time off. Slot availability is
          still affected by existing blocks even though they aren't shown here.
        </div>
      ) : null}

      {/* Body: matrix + detail rail */}
      <div className="flex min-h-0 flex-1">
        <section className="flex min-w-0 flex-1 flex-col">
          <TimeOffHeatmapMatrix
            groups={groups}
            year={year}
            activeCell={activeCell}
            onSelectCell={setActiveCell}
            isLoading={isLoading}
          />

          {/* Footer / legend */}
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-border bg-muted/30 px-5 py-2 text-[11px] text-muted-foreground">
            <span>Click a cell to inspect that doctor × month</span>
          </div>
        </section>

        {activeDoctor ? (
          <DetailRail
            doctor={activeDoctor}
            month={activeCell.month}
            year={year}
            rows={activeRows}
            onAddTimeOff={canManage ? handleAddToActiveMonth : undefined}
            onEditEntry={canManage ? handleEdit : undefined}
            onDeleteEntry={canManage ? setDeleteTarget : undefined}
          />
        ) : null}
      </div>

      <TimeOffFormSheet
        open={timeOffForm.open}
        onOpenChange={(next) =>
          setTimeOffForm((current) => ({ ...current, open: next, entry: next ? current.entry : null }))
        }
        mode={timeOffForm.mode}
        entry={timeOffForm.entry}
        selectedDate={timeOffForm.selectedDate}
        defaultScope={timeOffForm.defaultScope}
        doctorOptions={doctorOptions}
        tenantOptions={tenantOptions}
        onSaved={() => {
          setRefreshKey((value) => value + 1);
          publishCalendarRefresh({ source: "time-off-save" });
        }}
      />

      <ImportHolidaysDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        tenantId={importDefaults?.id || ""}
        defaultCountry={importDefaults?.country || ""}
        onImported={() => {
          setRefreshKey((value) => value + 1);
          publishCalendarRefresh({ source: "time-off-import" });
        }}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-500" aria-hidden />
              Delete time off
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.source === "imported"
                ? "This soft-deletes the imported holiday. It stays removed even if you re-import the same year."
                : "This soft-deletes the entry. Slot availability will return to normal for the affected days."}
            </DialogDescription>
          </DialogHeader>
          {deleteTarget ? (
            <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm">
              <p className="font-medium text-foreground">{deleteTarget.title}</p>
              <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                {formatEntryRange(deleteTarget)}
                {deleteTarget.doctor ? ` · ${deleteTarget.doctor}` : ""}
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
              aria-busy={isDeleting}
            >
              {isDeleting ? <Spinner className="mr-2 size-4" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TimeOffHeatmapPage;
